import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaDraftingCompass } from '@react-icons/all-files/fa/FaDraftingCompass';
import { FaProjectDiagram }  from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog }             from '@react-icons/all-files/fa/FaCog';
import { FaHome }            from '@react-icons/all-files/fa/FaHome';
import { FaCheck }           from '@react-icons/all-files/fa/FaCheck';
import { FaTimes }           from '@react-icons/all-files/fa/FaTimes';
import Breadcrumb    from '../../../../commons/Breadcrumb';
import PageHeader    from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm }     from '../../../../commons/Confirm';
import { EditButton, DeleteButton, DesignButton, RenderActions } from '../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../manager/workflow-diseno.reducer';

const BASE = '/configuracion/workflow/workflow-diseno';

const boolCell = (params: any) => params.value ? <FaCheck color="#22c55e" /> : <FaTimes color="#ef4444" />;

const objCell = (params: any) => {
  if (params.field === 'activo' || params.field === 'aprobado') return boolCell(params);
  return params.value?.nombre ?? String(params.value ?? '—');
};

export default function WorkflowDisenoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef   = useRef<HTMLDivElement>(null);

  const entities      = useAppSelector(s => s.workflowDiseno.entities);
  const totalItems    = useAppSelector(s => s.workflowDiseno.totalItems);
  const loading       = useAppSelector(s => s.workflowDiseno.loading);
  const updating      = useAppSelector(s => s.workflowDiseno.updating);
  const updateSuccess = useAppSelector(s => s.workflowDiseno.updateSuccess);

  const [rows, setRows]             = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast }                               = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Diseño eliminado correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => {
    const workflowId = params.row.profaseWorkflow?.id
      ?? params.row.profaseWorkflowEmpresa?.profaseWorkflow?.id;

    return (
    <RenderActions>
      {workflowId && (
        <DesignButton
          title="Ir al diseño"
          onClick={() => navigate(`/configuracion/workflow/definicion-workflow/${workflowId}/diseno`)}
        />
      )}
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton onClick={() => {
        const label = params.row.nombre ?? `#${params.row.id}`;
        ask(
          <>¿Confirmas que deseas eliminar el diseño <strong>{label}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar Diseño', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
    );
  };

  const cols = [
    { field: 'id',                     headerName: 'ID',              flex: 0.4 },
    { field: 'profaseWorkflow', headerName: 'Workflow', flex: 2.5, renderCell: objCell },
    { field: 'activo',                 headerName: 'Activo',           flex: 0.6, renderCell: objCell },
    { field: 'aprobado',               headerName: 'Aprobado',         flex: 0.7, renderCell: objCell },
    { field: '__actions',              headerName: '',                  flex: 0.7, renderCell: renderActions },
  ];

  const filters = [
    { label: 'Workflow',  name: 'workflowNombre' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio',          to: '/',                       icon: <FaHome /> },
        { label: 'Configuración',   to: '/configuracion',          icon: <FaCog /> },
        { label: 'Workflow',        to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Workflow Diseño',                                icon: <FaDraftingCompass /> },
      ]} />

      <PageHeader
        icon={<FaDraftingCompass />}
        iconColor="#ec4899"
        title="Workflow Diseño"
        subtitle="Listado de diseños de workflow"
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
